/**
 * HourglassStatsTab
 *
 * Hourglass-internal metrics: how much time attorneys are exporting,
 * leaving unexported, or deleting — plus active user count and
 * high/low exporter breakdowns.
 *
 * Sections:
 * - Timeframe selector
 * - 4 KPI gradient cards (Exported, Unexported, Deleted, Active Users)
 * - Export Activity chart (all attorneys sorted by export % descending)
 */

import { useState } from "react";
import { useExportStats, useDashboardMetrics } from "../../hooks/useAdminData";
import { getDateRangeFromTimeframe } from "../../types/dashboard";
import ExportActivityChart from "./ExportActivityChart";
import { KPI_CARD_STYLES } from "../../config/dashboardTheme";
import loaderStyles from "@/shared/components/LoadingPopup/LoadingPopup.module.css";

// ─────────────────────────────────────────────
// HourglassStatsTab
// ─────────────────────────────────────────────

export default function HourglassStatsTab() {
  const [timeframe, setTimeframe] = useState("30days");
  const { startDate, endDate } = getDateRangeFromTimeframe(timeframe);

  const {
    attorneys,
    loading: exportLoading,
    isPlaceholderData: exportIsPlaceholder,
  } = useExportStats(timeframe);
  const {
    metrics,
    loading: metricsLoading,
    isPlaceholderData: metricsIsPlaceholder,
  } = useDashboardMetrics(startDate, endDate);

  // Hard loading: first-ever fetch — show dashes in cards, loading in chart
  const hardLoading = (exportLoading || metricsLoading) && !exportIsPlaceholder && !metricsIsPlaceholder;

  // Soft loading: timeframe changed, old data visible at reduced opacity
  const isShowingPlaceholder = exportIsPlaceholder || metricsIsPlaceholder;

  return (
    <div className="space-y-6">
      {/* Centered spinner — first load only */}
      {hardLoading && (
        <div className="flex items-center justify-center min-h-[70vh]">
          <div className={loaderStyles.loader} />
        </div>
      )}

      {/* Content — dimmed while old timeframe data shows during a filter change */}
      {!hardLoading && (
        <div
          className={`space-y-6 transition-opacity duration-200${isShowingPlaceholder ? " opacity-60" : ""}`}
        >
          {/* KPI Cards */}
          <div className="flex flex-wrap gap-3">
              <div
                className={`relative overflow-hidden rounded-xl border-0 shadow-lg bg-gradient-to-br ${KPI_CARD_STYLES.green} p-4 min-w-[150px]`}
              >
                <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <p className="text-xs font-medium text-white/70 mb-1">Exported</p>
                <p className="text-2xl font-bold text-white">
                  {metrics?.exportedHours?.toFixed(1) ?? "0"}
                  <span className="text-sm font-normal text-white/70 ml-1">hrs</span>
                </p>
              </div>

              <div
                className={`relative overflow-hidden rounded-xl border-0 shadow-lg bg-gradient-to-br ${KPI_CARD_STYLES.teal} p-4 min-w-[150px]`}
              >
                <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <p className="text-xs font-medium text-white/70 mb-1">Unexported</p>
                <p className="text-2xl font-bold text-white">
                  {metrics?.unexportedHours?.toFixed(1) ?? "0"}
                  <span className="text-sm font-normal text-white/70 ml-1">hrs</span>
                </p>
              </div>

              <div
                className={`relative overflow-hidden rounded-xl border-0 shadow-lg bg-gradient-to-br ${KPI_CARD_STYLES.blue} p-4 min-w-[150px]`}
              >
                <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <p className="text-xs font-medium text-white/70 mb-1">Deleted</p>
                <p className="text-2xl font-bold text-white">
                  {metrics?.deletedHours?.toFixed(1) ?? "0"}
                  <span className="text-sm font-normal text-white/70 ml-1">hrs</span>
                </p>
              </div>

              <div
                className={`relative overflow-hidden rounded-xl border-0 shadow-lg bg-gradient-to-br ${KPI_CARD_STYLES.dark} p-4 min-w-[150px]`}
              >
                <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <p className="text-xs font-medium text-white/70 mb-1">Active Users</p>
                <p className="text-2xl font-bold text-white">
                  {metrics?.activeUsers ?? "0"}
                </p>
              </div>
          </div>

          {/* Export Activity Chart */}
          <ExportActivityChart
            attorneys={attorneys}
            loading={false}
            timeframe={timeframe}
            onTimeframeChange={setTimeframe}
          />
        </div>
      )}
    </div>
  );
}
